import type { MediaAsset } from '../types';

export interface DraftMedia { id: string; blob: Blob; kind: MediaAsset['kind']; duration?: number; optimized?: boolean }
export interface MomentDraft {
  id: string; uid: string; tripId: string; momentId: string; dayId: string; stopId?: string;
  text: string; occurredAt: string; media: DraftMedia[]; pending: boolean; error?: string;
}
type StoredDraft = Omit<MomentDraft, 'media'> & { media: Array<Omit<DraftMedia, 'blob'> & { storageId?: string; blob?: Blob; bytes?: ArrayBuffer; contentType?: string }> };
export type MomentDraftSummary = Omit<MomentDraft, 'media'> & { mediaCount: number };

let database: Promise<IDBDatabase> | undefined;
function draftsDB() {
  database ??= new Promise((resolve, reject) => {
    const request = indexedDB.open('tragenda-moment-drafts', 2);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains('drafts')) request.result.createObjectStore('drafts', { keyPath: 'id' }).createIndex('uid', 'uid');
      if (!request.result.objectStoreNames.contains('media')) request.result.createObjectStore('media', { keyPath: 'id' });
    };
    request.onsuccess = () => { request.result.onversionchange = () => { request.result.close(); database = undefined; }; resolve(request.result); };
    request.onerror = () => { database = undefined; reject(request.error); };
  });
  return database;
}
function notify() { window.dispatchEvent(new Event('tragenda:moment-drafts')); }
const writes = new Map<string, Promise<void>>();
function writeDraft(id: string, work: () => Promise<void>) {
  const operation = (writes.get(id) ?? Promise.resolve()).catch(() => undefined).then(work).finally(() => { if (writes.get(id) === operation) writes.delete(id); });
  writes.set(id, operation); return operation;
}
export function saveMomentDraft(draft: MomentDraft) { return writeDraft(draft.id, async () => {
  const database = await draftsDB();
  const references = draft.media.map(media => ({ media, storageId: `${draft.uid}:${draft.id}:${media.id}` }));
  const known = await Promise.all(references.map(({ storageId }) => new Promise<boolean>((resolve, reject) => {
    const request = database.transaction('media').objectStore('media').getKey(storageId);
    request.onsuccess = () => resolve(request.result !== undefined); request.onerror = () => reject(request.error);
  })));
  // Persist media once; caption edits only write small metadata, not entire videos.
  const additions = await Promise.all(references.filter((_, index) => !known[index]).map(async ({ media, storageId }) => ({ id: storageId, bytes: await media.blob.arrayBuffer(), contentType: media.blob.type })));
  const stored: StoredDraft = { ...draft, media: references.map(({ media: { blob, ...media }, storageId }) => ({ ...media, storageId, contentType: blob.type })) };
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(['drafts', 'media'], 'readwrite'), drafts = transaction.objectStore('drafts'), media = transaction.objectStore('media');
    const previous = drafts.get(draft.id);
    previous.onsuccess = () => {
      for (const item of (previous.result as StoredDraft | undefined)?.media ?? []) if (item.storageId && !stored.media.some(next => next.storageId === item.storageId)) media.delete(item.storageId);
      additions.forEach(item => media.put(item)); drafts.put(stored);
    };
    transaction.oncomplete = () => resolve(); transaction.onerror = transaction.onabort = () => reject(transaction.error ?? new Error('The private draft could not be saved.'));
  });
  notify();
}); }
async function storedDrafts(uid: string, tripId?: string): Promise<StoredDraft[]> {
  const database = await draftsDB();
  return new Promise((resolve, reject) => {
    const request = database.transaction('drafts').objectStore('drafts').index('uid').getAll(uid);
    request.onsuccess = () => resolve((request.result as StoredDraft[]).filter(draft => !tripId || draft.tripId === tripId));
    request.onerror = () => reject(request.error);
  });
}
async function hydrate(draft: StoredDraft): Promise<MomentDraft> {
  const database = await draftsDB();
  const media = await Promise.all(draft.media.map(async ({ storageId, bytes, contentType, blob, ...item }) => {
    if (!storageId) return { ...item, blob: blob ?? new Blob([bytes!], { type: contentType }) }; // Version 1 drafts remain readable.
    const stored = await new Promise<{ bytes: ArrayBuffer; contentType: string }>((resolve, reject) => {
      const request = database.transaction('media').objectStore('media').get(storageId);
      request.onsuccess = () => request.result ? resolve(request.result) : reject(new Error('Draft media is unavailable.')); request.onerror = () => reject(request.error);
    });
    return { ...item, blob: new Blob([stored.bytes], { type: stored.contentType }) };
  }));
  return { ...draft, media };
}
export async function listMomentDrafts(uid: string, tripId?: string): Promise<MomentDraft[]> { return Promise.all((await storedDrafts(uid, tripId)).map(hydrate)); }
export async function listMomentDraftSummaries(uid: string, tripId?: string): Promise<MomentDraftSummary[]> { return (await storedDrafts(uid, tripId)).map(({ media, ...draft }) => ({ ...draft, mediaCount: media.length })); }
export async function getMomentDraft(id: string, uid: string, tripId?: string): Promise<MomentDraft | null> {
  const database = await draftsDB();
  const draft = await new Promise<StoredDraft | undefined>((resolve, reject) => {
    const request = database.transaction('drafts').objectStore('drafts').get(id);
    request.onsuccess = () => resolve(request.result); request.onerror = () => reject(request.error);
  });
  return draft && draft.uid === uid && (!tripId || draft.tripId === tripId) ? hydrate(draft) : null;
}
export function discardMomentDraft(id: string) { return writeDraft(id, async () => {
  const database = await draftsDB();
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(['drafts', 'media'], 'readwrite'), drafts = transaction.objectStore('drafts'), media = transaction.objectStore('media');
    const request = drafts.get(id); request.onsuccess = () => { for (const item of (request.result as StoredDraft | undefined)?.media ?? []) if (item.storageId) media.delete(item.storageId); drafts.delete(id); };
    transaction.oncomplete = () => resolve(); transaction.onerror = () => reject(transaction.error);
  });
  notify();
}); }


export async function clearMomentDrafts(uid: string) {
  await Promise.allSettled([...writes.values()]);
  for (const draft of await listMomentDraftSummaries(uid)) await discardMomentDraft(draft.id);
}
